import React, { useState, useMemo } from 'react';
import { TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent,
} from '@/components/ui/dialog';
import {
  AlertCircle, AlertTriangle, BarChart3, CalendarClock, Copy, Eye,
  FileText, History, Info, Loader2, Phone, RotateCcw, Send, Share2,
  CheckCircle2, XCircle, ChevronDown, ChevronUp, Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { Section } from './helpers';
import { TabLoader } from './TabLoader';
import { inspectionsApi } from '@/services/api';

/**
 * OtpActivityLog - Combined view of Portal Events & SMS Logs grouped by OTP
 * Groups all activity by OTP code for easy tracing of each OTP request's lifecycle
 */
const OtpActivityLog = ({ events = [], smsLogs = [], otpSmsLogs = [] }) => {
  const [showApiDetails, setShowApiDetails] = useState({});

  // Combine and group all data by OTP
  const groupedByOtp = useMemo(() => {
    const groups = {};
    
    // Process portal events (otp_sent, wrong_phone_attempt)
    events.forEach(evt => {
      if (evt.event === 'otp_sent' && evt.otp) {
        const key = evt.otp;
        if (!groups[key]) {
          groups[key] = { 
            otp: evt.otp, 
            phone: evt.phone,
            events: [], 
            smsLogs: [],
            latestTimestamp: null
          };
        }
        groups[key].events.push({
          type: 'otp_sent',
          timestamp: evt.timestamp,
          phone: evt.phone,
          otp: evt.otp,
        });
        const ts = new Date(evt.timestamp).getTime();
        if (!groups[key].latestTimestamp || ts > groups[key].latestTimestamp) {
          groups[key].latestTimestamp = ts;
        }
      } else if (evt.event === 'wrong_phone_attempt') {
        const key = '_errors';
        if (!groups[key]) {
          groups[key] = { otp: null, phone: null, events: [], smsLogs: [], latestTimestamp: null };
        }
        groups[key].events.push({
          type: 'wrong_phone_attempt',
          timestamp: evt.timestamp,
          input_phone: evt.input_phone,
          expected_phone_masked: evt.expected_phone_masked,
        });
        const ts = new Date(evt.timestamp).getTime();
        if (!groups[key].latestTimestamp || ts > groups[key].latestTimestamp) {
          groups[key].latestTimestamp = ts;
        }
      }
    });

    // Process sms_logs (from sms_logs collection - PORTAL_OTP)
    smsLogs.forEach(log => {
      const otp = log.variables;
      if (otp) {
        const key = otp;
        if (!groups[key]) {
          groups[key] = { 
            otp: otp, 
            phone: log.phone || log.phone_masked,
            events: [], 
            smsLogs: [],
            latestTimestamp: null
          };
        }
        groups[key].smsLogs.push({
          source: 'sms_logs',
          timestamp: log.timestamp,
          phone: log.phone,
          phone_masked: log.phone_masked,
          template_id: log.template_id,
          request_id: log.request_id,
          success: log.success,
          response_data: log.response_data,
          error_message: log.error_message,
        });
        const ts = new Date(log.timestamp).getTime();
        if (!groups[key].latestTimestamp || ts > groups[key].latestTimestamp) {
          groups[key].latestTimestamp = ts;
        }
      }
    });

    // Process otp_sms_logs (from inspection_otp_logs collection)
    otpSmsLogs.forEach(log => {
      if (log.otp) {
        const key = log.otp;
        if (!groups[key]) {
          groups[key] = { 
            otp: log.otp, 
            phone: log.phone || log.phone_masked,
            events: [], 
            smsLogs: [],
            latestTimestamp: null
          };
        }
        groups[key].smsLogs.push({
          source: 'inspection_otp_logs',
          timestamp: log.sent_at,
          phone: log.phone,
          phone_masked: log.phone_masked,
          template_id: log.template_id,
          sender_id: log.sender_id,
          request_id: log.fast2sms_request_id,
          success: log.sms_sent,
          response_data: log.fast2sms_response,
          error: log.error,
          is_resend: log.is_resend,
          sent_by_name: log.sent_by_name,
        });
        const ts = new Date(log.sent_at).getTime();
        if (!groups[key].latestTimestamp || ts > groups[key].latestTimestamp) {
          groups[key].latestTimestamp = ts;
        }
      }
    });

    return Object.entries(groups)
      .map(([key, data]) => ({ key, ...data }))
      .sort((a, b) => (b.latestTimestamp || 0) - (a.latestTimestamp || 0));
  }, [events, smsLogs, otpSmsLogs]);

  const toggleApiDetails = (key) => {
    setShowApiDetails(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (groupedByOtp.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500 text-sm">
        No OTP activity recorded yet
      </div>
    );
  }

  return (
    <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
      {/* Error events (wrong phone attempts) */}
      {groupedByOtp.filter(g => g.key === '_errors').map((group) => (
        <div key="errors" className="rounded-lg border border-red-200 bg-red-50 p-3">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-red-500" />
            <span className="font-medium text-red-700 text-sm">Wrong Phone Attempts ({group.events.length})</span>
          </div>
          <div className="space-y-1">
            {group.events.slice(0, 3).map((evt, idx) => (
              <div key={`err-${idx}`} className="text-xs text-red-600 flex justify-between items-center">
                <span>
                  Entered: <code className="font-mono bg-white px-1 rounded">{evt.input_phone}</code>
                  <span className="text-red-400 mx-1">→</span>
                  Expected: <code className="font-mono bg-white px-1 rounded">{evt.expected_phone_masked}</code>
                </span>
                <span className="text-red-400 text-[10px]">{new Date(evt.timestamp).toLocaleString()}</span>
              </div>
            ))}
            {group.events.length > 3 && (
              <p className="text-xs text-red-400">+{group.events.length - 3} more attempts</p>
            )}
          </div>
        </div>
      ))}

      {/* OTP entries */}
      {groupedByOtp.filter(g => g.key !== '_errors').map((group) => {
        const latestSmsLog = group.smsLogs[0];
        const isSuccess = !latestSmsLog || latestSmsLog.success !== false;
        const showDetails = showApiDetails[group.key];

        return (
          <div 
            key={group.key} 
            className={`rounded-lg border p-3 ${
              isSuccess ? 'border-gray-200 bg-white' : 'border-red-200 bg-red-50'
            }`}
          >
            {/* Header Row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-1.5 rounded ${isSuccess ? 'bg-blue-100' : 'bg-red-100'}`}>
                  <Phone className={`h-4 w-4 ${isSuccess ? 'text-blue-600' : 'text-red-600'}`} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <code className="bg-gray-100 px-2 py-0.5 rounded font-mono font-bold text-sm text-gray-800">
                      {group.otp}
                    </code>
                    <span className="text-xs text-gray-500">→ {group.phone}</span>
                  </div>
                </div>
              </div>
              <span className="text-xs text-gray-400">
                {group.latestTimestamp && new Date(group.latestTimestamp).toLocaleString()}
              </span>
            </div>

            {/* SMS Status */}
            {latestSmsLog && (
              <div className={`mt-2 p-2 rounded text-xs ${
                isSuccess ? 'bg-green-50 border border-green-100' : 'bg-red-50 border border-red-100'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isSuccess ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                    ) : (
                      <XCircle className="h-3.5 w-3.5 text-red-600" />
                    )}
                    <span className={`font-medium ${isSuccess ? 'text-green-700' : 'text-red-700'}`}>
                      {isSuccess ? 'SMS Delivered' : 'SMS Failed'}
                    </span>
                    {latestSmsLog.is_resend && (
                      <span className="px-1.5 py-0.5 bg-blue-100 rounded text-blue-600 text-[10px]">Resend</span>
                    )}
                  </div>
                  <span className="text-gray-400">{new Date(latestSmsLog.timestamp).toLocaleString()}</span>
                </div>
                
                <div className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-0.5 text-gray-600">
                  <div><span className="text-gray-400">Template:</span> {latestSmsLog.template_id || 'N/A'}</div>
                  {latestSmsLog.request_id && (
                    <div><span className="text-gray-400">Request ID:</span> <code className="font-mono text-[10px]">{latestSmsLog.request_id}</code></div>
                  )}
                  {latestSmsLog.sent_by_name && (
                    <div className="col-span-2"><span className="text-gray-400">Sent by:</span> {latestSmsLog.sent_by_name}</div>
                  )}
                </div>

                {/* Error message */}
                {(latestSmsLog.error || latestSmsLog.error_message) && (
                  <div className="mt-1.5 text-red-600">
                    <span className="font-medium">Error:</span> {latestSmsLog.error || latestSmsLog.error_message}
                  </div>
                )}

                {/* Toggle for API Response */}
                {latestSmsLog.response_data && (
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <button
                      onClick={() => toggleApiDetails(group.key)}
                      className="text-[10px] text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                      {showDetails ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                      {showDetails ? 'Hide API Response' : 'Show API Response'}
                    </button>
                    {showDetails && (
                      <pre className="mt-1 p-2 bg-gray-800 text-green-400 rounded text-[10px] overflow-x-auto">
                        {JSON.stringify(latestSmsLog.response_data, null, 2)}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Additional SMS logs (if more than 1) */}
            {group.smsLogs.length > 1 && (
              <details className="mt-2">
                <summary className="text-[10px] text-gray-400 cursor-pointer hover:text-gray-600">
                  +{group.smsLogs.length - 1} more SMS attempt(s)
                </summary>
                <div className="mt-1 space-y-1">
                  {group.smsLogs.slice(1).map((log, idx) => (
                    <div key={`sms-${group.key}-${idx}`} className={`p-1.5 rounded text-[10px] ${
                      log.success ? 'bg-green-50' : 'bg-red-50'
                    }`}>
                      <span className={log.success ? 'text-green-600' : 'text-red-600'}>
                        {log.success ? '✓' : '✗'} {new Date(log.timestamp).toLocaleString()}
                      </span>
                      {log.request_id && <span className="text-gray-400 ml-2">ID: {log.request_id}</span>}
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        );
      })}
    </div>
  );
};

const ReportTab = ({
  inspection, publishHistory, publishingReport, loadingPublishHistory,
  showPublishModal, setShowPublishModal, publishNotes, setPublishNotes,
  handlePublishReport, handlePreviewReport, fetchPublishHistory,
  portalAudit, hasUnsavedChanges,
}) => {
  const [resendingOtp, setResendingOtp] = useState(false);
  const [lastOtpResult, setLastOtpResult] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [pdfDropdownOpen, setPdfDropdownOpen] = useState(false);

  const handleDownloadPdf = async (brand = 'wisedrive') => {
    if (!inspection?.id) return;
    setDownloadingPdf(true);
    setPdfDropdownOpen(false);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Safari blocks programmatic downloads (a.click()) after an await
      // because the user gesture context is lost. Instead, open a new tab
      // IMMEDIATELY (preserving gesture context) that points to a single
      // endpoint which generates AND serves the PDF in one request.
      // Safari shows the tab loading while PDF generates, then downloads it.
      const directUrl = `${apiUrl}/api/inspections/${inspection.id}/report-pdf?brand=${brand}&token=${encodeURIComponent(token)}`;
      window.open(directUrl, '_blank');
      
      toast.success('PDF download started');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error(error.message || 'Failed to generate PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleResendOtp = async () => {
    if (!inspection?.id) return;
    
    setResendingOtp(true);
    setLastOtpResult(null);
    
    try {
      const response = await inspectionsApi.resendPortalOtp(inspection.id);
      const data = response.data;
      
      setLastOtpResult({
        success: data.success,
        otp: data.otp_details?.otp,
        phone: data.otp_details?.phone_masked,
        requestId: data.sms_details?.request_id,
        error: data.sms_details?.error,
        timestamp: new Date().toISOString()
      });
      
      if (data.success) {
        toast.success(`OTP ${data.otp_details?.otp} sent to ${data.otp_details?.phone_masked}`);
        // Refresh the audit logs
        if (fetchPublishHistory) {
          fetchPublishHistory();
        }
      } else {
        toast.error(`Failed to send OTP: ${data.sms_details?.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error resending OTP:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to resend OTP';
      setLastOtpResult({
        success: false,
        error: errorMsg,
        timestamp: new Date().toISOString()
      });
      toast.error(errorMsg);
    } finally {
      setResendingOtp(false);
    }
  };

  return (
    <>
            <TabsContent value="report" className="space-y-3 mt-0" data-testid="report-tab-content">
              {!inspection?.id ? <TabLoader message="Loading report..." /> : (<>
              {/* Report Actions */}
              <div className="flex items-center justify-between bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-200">
                <div>
                  <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-emerald-600" />
                    Inspection Report
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Preview and publish the inspection report to the customer
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {hasUnsavedChanges && (
                    <div className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                      ⚠️ Unsaved changes
                    </div>
                  )}
                  <Button
                    variant="outline"
                    onClick={handlePreviewReport}
                    className={`border-emerald-300 text-emerald-700 hover:bg-emerald-50 ${hasUnsavedChanges ? 'opacity-60' : ''}`}
                    data-testid="preview-report-btn"
                    title={hasUnsavedChanges ? 'Save changes first to preview the report' : 'Preview report'}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Preview Report
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                      const apiUrl = process.env.REACT_APP_BACKEND_URL;
                      const brand = inspection?.partner_id ? 'sheerdrive' : 'wisedrive';
                      window.open(
                        `${apiUrl}/api/inspections/${inspection.id}/report-html-preview?brand=${brand}&token=${encodeURIComponent(token)}`,
                        '_blank'
                      );
                    }}
                    className="border-orange-300 text-orange-700 hover:bg-orange-50"
                    data-testid="sheerdrive-preview-btn"
                    title="Open full report as HTML page (print-friendly)"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    PDF Preview
                  </Button>
                  <div className="relative" data-testid="download-pdf-dropdown">
                    <Button
                      variant="outline"
                      onClick={() => setPdfDropdownOpen(!pdfDropdownOpen)}
                      disabled={downloadingPdf}
                      className="border-blue-300 text-blue-700 hover:bg-blue-50"
                      data-testid="download-pdf-btn"
                    >
                      {downloadingPdf ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4 mr-2" />
                      )}
                      {downloadingPdf ? 'Generating...' : 'Download PDF'}
                      {!downloadingPdf && <ChevronDown className="h-3 w-3 ml-1" />}
                    </Button>
                    {pdfDropdownOpen && !downloadingPdf && (
                      <div className="absolute right-0 mt-1 w-52 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden">
                        <button
                          onClick={() => handleDownloadPdf('wisedrive')}
                          className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 flex items-center gap-2 transition-colors"
                          data-testid="download-wisedrive-pdf"
                        >
                          <div className="w-2 h-2 rounded-full bg-[#1e3a5f]" />
                          Wisedrive B2C Report
                        </button>
                        <button
                          onClick={() => handleDownloadPdf('sheerdrive')}
                          className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-orange-50 flex items-center gap-2 transition-colors border-t border-gray-100"
                          data-testid="download-sheerdrive-pdf"
                        >
                          <div className="w-2 h-2 rounded-full bg-[#E5843B]" />
                          Sheerdrive Report
                        </button>
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={() => setShowPublishModal(true)}
                    className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
                    data-testid="publish-report-btn"
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Publish Report
                  </Button>
                </div>
              </div>
              
              {/* Publish Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white rounded-xl p-4 border shadow-sm">
                  <div className="text-2xl font-bold text-emerald-600">{inspection?.publish_count || 0}</div>
                  <div className="text-sm text-gray-500">Total Publishes</div>
                </div>
                <div className="bg-white rounded-xl p-4 border shadow-sm">
                  <div className="text-2xl font-bold text-blue-600">
                    {inspection?.last_published_at 
                      ? new Date(inspection.last_published_at).toLocaleDateString() 
                      : '—'}
                  </div>
                  <div className="text-sm text-gray-500">Last Published</div>
                </div>
                <div className="bg-white rounded-xl p-4 border shadow-sm">
                  <div className="text-2xl font-bold text-purple-600">
                    {inspection?.report_published ? '✓ Published' : 'Not Published'}
                  </div>
                  <div className="text-sm text-gray-500">Status</div>
                </div>
              </div>
              
              {/* Publish History + Portal Audit - INTERNAL AUDIT LOG */}
              <Section 
                title="Internal Audit Log" 
                icon={History} 
                defaultOpen={true} 
                badge={`${publishHistory.length} entries`}
              >
                <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                  <strong>🔒 Internal Only:</strong> This audit log is for internal tracking purposes only and is NOT visible to customers.
                </div>
                {loadingPublishHistory ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                  </div>
                ) : publishHistory.length > 0 ? (
                  <div className="space-y-4">
                    {publishHistory.map((entry, idx) => (
                      <div 
                        key={entry.id} 
                        className={`p-4 rounded-lg border-l-4 ${
                          idx === 0 ? 'bg-emerald-50 border-l-emerald-500' : 'bg-gray-50 border-l-gray-300'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-gray-900">
                                Publish #{publishHistory.length - idx}
                              </span>
                              {idx === 0 && (
                                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                                  Latest
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                              <span className="flex items-center gap-1">
                                <CalendarClock className="h-3 w-3" />
                                {new Date(entry.published_at).toLocaleString()}
                              </span>
                            </div>
                            <div className="text-sm text-gray-500 mt-0.5">
                              By: {entry.published_by_name}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {entry.send_whatsapp && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                WhatsApp Sent
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* User Comment */}
                        {(entry.user_comment || entry.notes) && (
                          <div className="mt-2 p-2 bg-white rounded border text-sm">
                            <span className="text-gray-500 font-medium">Comment:</span> {entry.user_comment || entry.notes}
                          </div>
                        )}

                        {/* Report Link */}
                        {entry.report_url && (
                          <div className="mt-2 p-3 bg-white rounded-lg border border-gray-200 space-y-2">
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-gray-500 font-medium">Report Link:</span>
                              <a 
                                href={entry.report_url.replace(/^https?:\/\/[^/]+/, window.location.origin)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-700 underline break-all"
                              >
                                {entry.report_url}
                              </a>
                              <button
                                onClick={() => {
                                  const url = entry.report_url.replace(/^https?:\/\/[^/]+/, window.location.origin);
                                  navigator.clipboard.writeText(url);
                                  toast.success('Report link copied!');
                                }}
                                className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                              >
                                <Copy className="h-3.5 w-3.5" />
                              </button>
                            </div>
                            {entry.customer_phone && (
                              <div className="flex items-center gap-2 text-sm">
                                <span className="text-gray-500 font-medium">Customer Phone:</span>
                                <span className="text-gray-700">{entry.customer_phone}</span>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Changes - Auto-detected */}
                        {entry.changes && entry.changes.length > 0 && (
                          <div className="mt-3">
                            <div className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                              <BarChart3 className="h-3 w-3" />
                              Auto-detected changes in this version:
                            </div>
                            <div className="space-y-1 max-h-48 overflow-y-auto">
                              {entry.changes.map((change, cIdx) => (
                                <div 
                                  key={cIdx} 
                                  className={`flex items-start gap-2 text-sm p-2 rounded ${
                                    change.type === 'created' ? 'bg-blue-50 text-blue-700' :
                                    change.type === 'updated' ? 'bg-amber-50 text-amber-700' :
                                    change.type === 'republished' ? 'bg-gray-50 text-gray-600' :
                                    'bg-gray-50 text-gray-700'
                                  }`}
                                >
                                  <span className={`h-1.5 w-1.5 rounded-full mt-1.5 flex-shrink-0 ${
                                    change.type === 'created' ? 'bg-blue-500' :
                                    change.type === 'updated' ? 'bg-amber-500' :
                                    'bg-gray-400'
                                  }`}></span>
                                  <div>
                                    <span className="font-medium">{change.field}</span>
                                    <span className="text-gray-500 mx-1">—</span>
                                    <span className="text-gray-600">{change.details}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <History className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                    <p className="font-medium">No Publish History</p>
                    <p className="text-sm">This report has not been published to the customer yet</p>
                  </div>
                )}

                {/* OTP Activity Log - Combined Portal Events & SMS Logs grouped by OTP */}
                {(portalAudit.events?.length > 0 || portalAudit.active_otp || portalAudit.sms_logs?.length > 0 || portalAudit.otp_sms_logs?.length > 0) && (
                  <div className="mt-4 border-t pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                        <Phone className="h-4 w-4" />
                        OTP Activity Log
                      </h4>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={fetchPublishHistory}
                        className="h-7 text-xs"
                        data-testid="refresh-audit-btn"
                      >
                        <RotateCcw className="h-3 w-3 mr-1" />
                        Refresh
                      </Button>
                    </div>

                    {/* Active OTP */}
                    {portalAudit.active_otp && (
                      <div className="mb-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-amber-700 font-medium">Active OTP:</span>
                            <code className="bg-white px-2 py-0.5 rounded font-mono text-amber-800 font-bold border border-amber-200">
                              {portalAudit.active_otp.otp}
                            </code>
                            <span className="text-xs text-amber-600">
                              for {portalAudit.active_otp.phone}
                            </span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleResendOtp}
                            disabled={resendingOtp}
                            className="h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-100"
                            data-testid="resend-otp-btn"
                          >
                            {resendingOtp ? (
                              <>
                                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                Sending...
                              </>
                            ) : (
                              <>
                                <Send className="h-3 w-3 mr-1" />
                                Resend OTP
                              </>
                            )}
                          </Button>
                        </div>
                        {portalAudit.active_otp.created_at && (
                          <p className="text-xs text-amber-500 mt-1">
                            Created: {new Date(portalAudit.active_otp.created_at).toLocaleString()}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Last OTP Result (from Resend) */}
                    {lastOtpResult && (
                      <div className={`mb-3 p-3 rounded-lg border ${
                        lastOtpResult.success 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-red-50 border-red-200'
                      }`}>
                        <div className="flex items-center gap-2 text-sm">
                          {lastOtpResult.success ? (
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                          <span className={lastOtpResult.success ? 'text-green-700' : 'text-red-700'}>
                            {lastOtpResult.success ? 'OTP Sent Successfully' : 'Failed to Send OTP'}
                          </span>
                        </div>
                        {lastOtpResult.success && (
                          <div className="mt-2 text-xs space-y-1">
                            <p className="text-green-600">
                              <span className="font-medium">OTP:</span>{' '}
                              <code className="bg-white px-1.5 py-0.5 rounded font-mono font-bold">{lastOtpResult.otp}</code>
                            </p>
                            <p className="text-green-600">
                              <span className="font-medium">Phone:</span> {lastOtpResult.phone}
                            </p>
                            {lastOtpResult.requestId && (
                              <p className="text-green-500">
                                <span className="font-medium">Fast2SMS Request ID:</span> {lastOtpResult.requestId}
                              </p>
                            )}
                          </div>
                        )}
                        {!lastOtpResult.success && lastOtpResult.error && (
                          <p className="mt-1 text-xs text-red-600">
                            <span className="font-medium">Error:</span> {lastOtpResult.error}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(lastOtpResult.timestamp).toLocaleString()}
                        </p>
                      </div>
                    )}

                    {/* No Active OTP - Show Send OTP button */}
                    {!portalAudit.active_otp && publishHistory?.length > 0 && (
                      <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-gray-600">No active OTP for this customer</p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleResendOtp}
                            disabled={resendingOtp}
                            className="h-7 text-xs"
                            data-testid="send-otp-btn"
                          >
                            {resendingOtp ? (
                              <>
                                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                Sending...
                              </>
                            ) : (
                              <>
                                <Send className="h-3 w-3 mr-1" />
                                Send OTP
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Combined OTP Activity Log - Grouped by OTP */}
                    <OtpActivityLog 
                      events={portalAudit.events || []}
                      smsLogs={portalAudit.sms_logs || []}
                      otpSmsLogs={portalAudit.otp_sms_logs || []}
                    />
                  </div>
                )}
              </Section>
              
              {/* Publish Modal */}
              {showPublishModal && (
                <Dialog open={showPublishModal} onOpenChange={setShowPublishModal}>
                  <DialogContent className="sm:max-w-md">
                    <div className="p-4">
                      <h3 className="text-lg font-semibold flex items-center gap-2">
                        <Share2 className="h-5 w-5 text-emerald-600" />
                        Publish Report
                      </h3>
                      <p className="text-sm text-gray-600 mt-2">
                        Publishing will send the report to the customer via WhatsApp. 
                        All changes since last publish will be <strong>automatically detected and logged</strong>.
                      </p>
                      
                      <div className="mt-4">
                        <Label className="text-sm font-medium">Your Comment (optional)</Label>
                        <p className="text-xs text-gray-500 mb-1">Add a personal note about this publish - this will be visible in version history</p>
                        <textarea
                          value={publishNotes}
                          onChange={(e) => setPublishNotes(e.target.value)}
                          placeholder="e.g., 'Updated exterior rating after customer feedback' or 'Final report after repairs verified'"
                          className="w-full mt-1 p-2 border rounded-lg text-sm min-h-[80px] resize-none"
                        />
                      </div>
                      
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg text-sm">
                        <div className="flex items-start gap-2">
                          <Info className="h-4 w-4 text-blue-600 mt-0.5" />
                          <div>
                            <p className="font-medium text-blue-800">Auto-tracked changes</p>
                            <p className="text-blue-600 text-xs">
                              Customer info, vehicle details, condition ratings, repairs, insurance, and all other edits will be automatically recorded.
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-4 p-3 bg-amber-50 rounded-lg text-sm">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
                          <div>
                            <p className="font-medium text-amber-800">This will notify the customer</p>
                            <p className="text-amber-600">
                              A WhatsApp message and SMS with the report link will be sent to {inspection?.customer_mobile || inspection?.customer_phone || 'the customer'}.
                            </p>
                            <p className="text-amber-600 text-xs mt-1">
                              Customer will need to verify their phone number with OTP to view the report.
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex justify-end gap-2 mt-4">
                        <Button 
                          variant="outline" 
                          onClick={() => setShowPublishModal(false)}
                          disabled={publishingReport}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handlePublishReport}
                          disabled={publishingReport}
                          className="bg-gradient-to-r from-emerald-600 to-teal-600"
                          data-testid="confirm-publish-btn"
                        >
                          {publishingReport ? (
                            <><Loader2 className="h-4 w-4 animate-spin mr-2" />Publishing...</>
                          ) : (
                            <><Share2 className="h-4 w-4 mr-2" />Publish Now</>
                          )}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
              </>)}
            </TabsContent>

    </>
  );
};

export default ReportTab;
